import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { collection, onSnapshot } from 'firebase/firestore';

const ProjectContext = createContext();

export function useProject() {
  return useContext(ProjectContext);
}

export function ProjectProvider({ children }) {
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setProjects([]);
      setCurrentProject(null);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(collection(db, "projects"), (snapshot) => {
      let allProjects = [];
      snapshot.forEach(doc => {
        allProjects.push({ id: doc.id, ...doc.data() });
      });
      
      // Filter projects based on user assignment if not Admin or Super Admin
      let userProjects = allProjects;
      if (!['Admin', 'Super Admin'].includes(currentUser.role)) {
        const assigned = currentUser.assignedProjects || [];
        userProjects = allProjects.filter(p => assigned.includes(p.id));
      }

      setProjects(userProjects);
      
      // Auto-select project if none selected or if current selection is invalid
      if (userProjects.length > 0) {
        setCurrentProject((prev) => {
          if (!prev || !userProjects.find(p => p.id === prev.id)) {
            return userProjects[0];
          }
          return prev;
        });
      } else {
        setCurrentProject(null);
      }
      
      // Auto-backfill po_management into existing project documents if logged in as Admin
      if (['Admin', 'Super Admin'].includes(currentUser?.role)) {
        snapshot.forEach(async (projDoc) => {
          const pData = projDoc.data();
          if (Array.isArray(pData.enabledModules) && !pData.enabledModules.includes('po_management')) {
            try {
              const { updateDoc, doc } = await import('firebase/firestore');
              await updateDoc(doc(db, "projects", projDoc.id), {
                enabledModules: [...pData.enabledModules, 'po_management']
              });
              console.log(`[ProjectContext] Added po_management to project: ${pData.projectName || projDoc.id}`);
            } catch (err) {
              console.warn(`[ProjectContext] Failed to backfill project ${projDoc.id}:`, err.message);
            }
          }
        });
      }
      
      setLoading(false);
    }, (error) => {
      console.error("Error fetching projects:", error);
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser]);

  const isModuleEnabledForProject = (moduleKey) => {
    // If no project is selected, modules are disabled (except global ones handled elsewhere)
    if (!currentProject) return false;
    
    // Legacy projects without the array default to everything enabled
    if (!currentProject.enabledModules || !Array.isArray(currentProject.enabledModules)) return true;
    
    // PO Management & Tender Intelligence are standard modules enabled across all projects unless explicitly disabled
    if (
      (moduleKey === 'po_management' && !currentProject.disabledModules?.includes('po_management')) ||
      (moduleKey === 'tender_intelligence' && !currentProject.disabledModules?.includes('tender_intelligence'))
    ) {
      return true;
    }

    return currentProject.enabledModules.includes(moduleKey);
  };

  const value = {
    projects,
    currentProject,
    setCurrentProject,
    isModuleEnabledForProject
  };

  return (
    <ProjectContext.Provider value={value}>
      {!loading && children}
    </ProjectContext.Provider>
  );
}
